import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getArticleBySlug, getCategoryBySlug, getArticlesByCategory, hotList } from "../../mocks/news";
import { usePageMeta, useJsonLd } from "../../hooks/useSeo";
import { CoverCard, ListRow, Breadcrumbs } from "../../components/base/NewsCard";

export default function NewsDetail() {
  const { slug = "" } = useParams();
  const article = getArticleBySlug(slug);
  const [copied, setCopied] = useState(false);

  const cat = article ? getCategoryBySlug(article.category) : undefined;
  const related = article ? getArticlesByCategory(article.category).filter((a) => a.slug !== article.slug).slice(0, 3) : [];
  const crossCat = article ? hotList.filter((a) => a.slug !== article.slug).slice(0, 6) : [];

  usePageMeta(
    article
      ? {
          title: `${article.title} - 每日爆料网`,
          description: article.summary,
          keywords: article.tags.join(","),
        }
      : { title: "内容不存在 - 每日爆料网" },
  );

  useJsonLd("article-breadcrumb", article
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://29nx.cn/" },
          { "@type": "ListItem", position: 2, name: article.categoryName, item: `https://29nx.cn/category/${article.category}` },
          { "@type": "ListItem", position: 3, name: article.title },
        ],
      }
    : null);

  useJsonLd("article", article
    ? {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        headline: article.title,
        description: article.summary,
        image: [article.cover],
        datePublished: article.date,
        dateModified: article.date,
        author: { "@type": "Organization", name: article.author },
        publisher: { "@type": "Organization", name: "每日爆料网" },
        keywords: article.tags.join(","),
        articleSection: article.categoryName,
        inLanguage: "zh-CN",
        mainEntityOfPage: `https://29nx.cn/news/${article.slug}`,
      }
    : null);

  if (!article || !cat) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-serif text-3xl font-black text-foreground-950">这篇文章不存在</h1>
        <p className="mt-3 text-foreground-600">可能已被调整或链接有误。</p>
        <Link to="/" className="mt-6 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
          返回首页 <i className="ri-arrow-right-line" />
        </Link>
      </div>
    );
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
        {/* 正文 */}
        <article className="lg:col-span-2">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: article.categoryName, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
          <header>
            <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-sm bg-primary-100 px-2.5 py-1 text-xs font-bold text-primary-700">
              <i className="ri-flashlight-fill" /> {cat.name}
            </span>
            <h1 className="mt-3 font-serif text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 text-[15px] leading-relaxed text-foreground-700 md:text-base">{article.summary}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-3 text-sm text-foreground-600">
              <span className="inline-flex items-center gap-1.5">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-100 text-xs font-bold text-secondary-900">
                  {article.author.slice(0, 1)}
                </span>
                <span className="font-medium text-foreground-900">{article.author}</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line" /> {article.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-eye-line" /> {article.views} 阅读
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line" /> 约 {article.readMinutes} 分钟
              </span>
            </div>
          </header>

          <div className="my-7 overflow-hidden rounded-xl">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - 每日爆料网`}
              className="aspect-[16/9] w-full object-cover"
            />
          </div>

          <div className="prose-detail">
            {article.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {/* 标签 */}
          <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-background-200 pt-5">
            <span className="text-sm font-medium text-foreground-500">标签：</span>
            {article.tags.map((t) => (
              <Link
                key={t}
                to={`/category/${article.category}`}
                className="cursor-pointer whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-sm text-foreground-700 transition-colors hover:bg-accent-100 hover:text-accent-800"
              >
                # {t}
              </Link>
            ))}
          </div>

          {/* 分享 */}
          <div className="mt-5 flex items-center justify-between rounded-xl border border-background-200 bg-background-50 p-4">
            <span className="text-sm text-foreground-600">觉得有用？分享给同样关注这些的人</span>
            <div className="flex items-center gap-2">
              {[
                { icon: "ri-wechat-fill", label: "微信" },
                { icon: "ri-weibo-line", label: "微博" },
                { icon: "ri-qq-line", label: "QQ" },
              ].map((s) => (
                <button
                  key={s.label}
                  aria-label={`分享到${s.label}`}
                  onClick={copyLink}
                  className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50"
                >
                  <i className={`${s.icon} text-lg`} />
                </button>
              ))}
              <button
                onClick={copyLink}
                className="flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className={copied ? "ri-check-line" : "ri-link"} />
                {copied ? "链接已复制" : "复制链接"}
              </button>
            </div>
          </div>

          {/* 相关阅读 */}
          {related.length > 0 && (
            <section className="mt-10">
              <h2 className="mb-5 font-serif text-xl font-black text-foreground-950">同栏目推荐阅读</h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((item) => (
                  <CoverCard key={item.slug} item={item} />
                ))}
              </div>
            </section>
          )}
        </article>

        {/* 侧栏 */}
        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-xl border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-serif text-lg font-black text-foreground-950">
              <i className="ri-fire-fill text-primary-500" /> 正在被围观
            </h2>
            <div className="mt-2 divide-y divide-background-200">
              {crossCat.map((a, i) => (
                <ListRow key={a.slug} item={a} index={i} />
              ))}
            </div>
          </div>
          <div className="rounded-xl bg-secondary-800 p-5 text-background-100">
            <h2 className="flex items-center gap-2 font-serif text-lg font-black text-accent-300">
              <i className="ri-bubble-chart-line" /> 意见征集
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-background-200">
              对我们的话题有不同看法或补充线索？欢迎告诉我们。
            </p>
            <Link to="/faq#contact" className="mt-3 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-accent-400 px-4 py-2 text-sm font-medium text-secondary-900 hover:bg-accent-300">
              联系我们 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}